import { Badge } from '@artic/ui/badge';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { FullHeightChrome, FullHeightFill, FullHeightStack } from '@artic/ui/full-height-layout';
import { Skeleton } from '@artic/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { ProjectPageHeader } from '../../features/project-planning/project-page-header';
import { deriveIsProjectRedrafting } from '../../features/project-planning/project-redrafting';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import { projectPlanningDetailQueryOptions } from '../../features/project-planning/queries';
import { ProjectTimeline } from '../../features/project-timeline/project-timeline';
import { ProjectTimelineViewBy } from '../../features/project-timeline/project-timeline-filter-bar';

export const Route = createFileRoute('/projects/$projectId_/timeline')({
  staticData: {
    breadcrumb: 'Timeline',
    layout: 'full-height',
  },
  component: ProjectTimelinePage,
});

function ProjectTimelinePage() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          Production Planning is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <ProjectTimelineContent />;
}

function ProjectTimelineContent() {
  const { projectId } = Route.useParams();

  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  if (projectLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project. Please try again.
        </p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <Badge variant="outline">Project not found</Badge>
      </div>
    );
  }

  const isProjectRedrafting = deriveIsProjectRedrafting(
    project.project_status,
    project.work_orders,
  );

  return (
    <FullHeightStack>
      <FullHeightChrome>
        <ProjectStepper
          projectId={project.project_id}
          active="work_orders"
          projectStatus={project.project_status}
          isProjectRedrafting={isProjectRedrafting}
        />
      </FullHeightChrome>
      <FullHeightChrome>
        <ProjectPageHeader project={project} activeView="timeline" />
      </FullHeightChrome>
      <FullHeightChrome className="flex items-center justify-end px-3 py-1.5">
        <ProjectTimelineViewBy />
      </FullHeightChrome>
      <FullHeightFill className="px-3 pb-3">
        <ProjectTimeline
          projects={[
            {
              project_id: project.project_id,
              project_name: project.project_name,
              project_derived_status: project.project_derived_status,
              project_scheduled_start_date: project.project_scheduled_start_date,
              project_scheduled_complete_date: project.project_scheduled_complete_date,
            },
          ]}
          applyStatusFilter={false}
        />
      </FullHeightFill>
    </FullHeightStack>
  );
}
